import { Button } from '../ui/button';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../ui/dialog';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { ScrollArea } from '../ui/scroll-area';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import {
  AlertTriangle, Archive, RotateCcw, Trash2, Mail, Loader2, UserPlus,
} from 'lucide-react';

// ============== Delete Confirmation ==============
export function DeleteDialog({ open, onOpenChange, user, onConfirm }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-destructive" /> Delete User
          </DialogTitle>
          <DialogDescription>
            Are you sure you want to delete <strong>{user?.name}</strong> ({user?.email})?
            Their account will be moved to deleted for 30 days before permanent deletion.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="gap-2">
          <Button variant="ghost" onClick={() => onOpenChange(false)} data-testid="delete-cancel-btn">Cancel</Button>
          <Button variant="destructive" onClick={onConfirm} data-testid="delete-confirm-btn">Delete User</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ============== Permanent Delete ==============
export function PermanentDeleteDialog({ open, onOpenChange, user, onConfirm }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-destructive" /> Permanently Delete User
          </DialogTitle>
          <DialogDescription>
            This will <strong>permanently</strong> delete <strong>{user?.name}</strong> ({user?.email}) and all their automata. This cannot be undone.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="gap-2">
          <Button variant="ghost" onClick={() => onOpenChange(false)} data-testid="permanent-delete-cancel-btn">Cancel</Button>
          <Button variant="destructive" onClick={onConfirm} data-testid="permanent-delete-confirm-btn">Permanently Delete</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ============== Individual Archive (notify choice) ==============
export function ArchiveDialog({ open, onOpenChange, user, onArchive }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Archive className="w-5 h-5 text-amber-500" /> Archive User
          </DialogTitle>
          <DialogDescription>
            Archive <strong>{user?.name}</strong> ({user?.email})?
            They can reactivate by logging in within 30 days. Would you like to notify them by email?
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="gap-2">
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button variant="outline" onClick={() => onArchive(false)} data-testid="archive-silent-btn">
            <Archive className="w-4 h-4 mr-2" /> Archive Silently
          </Button>
          <Button onClick={() => onArchive(true)} data-testid="archive-notify-btn">
            <Mail className="w-4 h-4 mr-2" /> Archive &amp; Notify
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ============== Bulk Archive ==============
export function BulkArchiveDialog({ open, onOpenChange, count, filterDescription, loading, onArchive }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Archive className="w-5 h-5 text-amber-500" /> Archive {count} User{count !== 1 ? 's' : ''}
          </DialogTitle>
          <DialogDescription>
            This will archive <strong>{count}</strong> user{count !== 1 ? 's' : ''} matching: <em>{filterDescription}</em>.
            They can reactivate by logging in within 30 days. Admin accounts are excluded.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="gap-2">
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button variant="outline" onClick={() => onArchive(false)} disabled={loading} data-testid="bulk-archive-silent-btn">
            {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Archive className="w-4 h-4 mr-2" />}
            Archive Silently
          </Button>
          <Button onClick={() => onArchive(true)} disabled={loading} data-testid="bulk-archive-notify-btn">
            {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Mail className="w-4 h-4 mr-2" />}
            Archive &amp; Notify
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ============== Bulk Delete ==============
export function BulkDeleteDialog({ open, onOpenChange, count, filterDescription, loading, onConfirm }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-destructive" /> Delete {count} User{count !== 1 ? 's' : ''}
          </DialogTitle>
          <DialogDescription>
            This will delete <strong>{count}</strong> user{count !== 1 ? 's' : ''} matching: <em>{filterDescription}</em>.
            Accounts will be moved to deleted status with a 30-day retention period. Admin accounts are excluded.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="gap-2">
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button variant="destructive" onClick={onConfirm} disabled={loading} data-testid="bulk-delete-confirm-btn">
            {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Trash2 className="w-4 h-4 mr-2" />}
            Delete All
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ============== Bulk Restore ==============
export function BulkRestoreDialog({ open, onOpenChange, count, filterDescription, loading, onConfirm }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <RotateCcw className="w-5 h-5 text-green-500" /> Restore {count} User{count !== 1 ? 's' : ''}
          </DialogTitle>
          <DialogDescription>
            This will restore <strong>{count}</strong> user{count !== 1 ? 's' : ''} matching: <em>{filterDescription}</em>.
            Their accounts and automata will be reactivated.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="gap-2">
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={onConfirm} disabled={loading} data-testid="bulk-restore-confirm-btn">
            {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <RotateCcw className="w-4 h-4 mr-2" />}
            Restore All
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ============== Bulk Permanent Delete ==============
export function BulkPermanentDeleteDialog({ open, onOpenChange, count, filterDescription, loading, onConfirm }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-destructive" /> Permanently Delete {count} User{count !== 1 ? 's' : ''}
          </DialogTitle>
          <DialogDescription>
            This will <strong>permanently</strong> delete <strong>{count}</strong> user{count !== 1 ? 's' : ''} matching: <em>{filterDescription}</em>.
            All accounts and automata will be wiped. This cannot be undone.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="gap-2">
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button variant="destructive" onClick={onConfirm} disabled={loading} data-testid="bulk-perm-delete-confirm-btn">
            {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Trash2 className="w-4 h-4 mr-2" />}
            Permanently Delete All
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ============== Send Message ==============
export function MessageDialog({ open, onOpenChange, targets, subject, setSubject, body, setBody, sending, onSend }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Mail className="w-5 h-5 text-primary" /> Send Email
          </DialogTitle>
          <DialogDescription>
            {targets.length === 1
              ? <>Send an email to <strong>{targets[0]?.name}</strong> ({targets[0]?.email})</>
              : <>Send an email to <strong>{targets.length} recipients</strong></>}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          {targets.length > 1 && (
            <div className="text-xs text-muted-foreground bg-muted rounded-md p-2 max-h-20 overflow-y-auto">
              {targets.map(t => t.email).join(', ')}
            </div>
          )}
          <div className="space-y-2">
            <Label htmlFor="msg-subject">Subject</Label>
            <Input
              id="msg-subject" value={subject} onChange={(e) => setSubject(e.target.value)}
              placeholder="Email subject" data-testid="message-subject"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="msg-body">Message</Label>
            <textarea
              id="msg-body" value={body} onChange={(e) => setBody(e.target.value)}
              placeholder="Email body..."
              className="w-full min-h-[120px] px-3 py-2 text-sm bg-background border border-border rounded-md focus:outline-none focus:ring-1 focus:ring-ring resize-y"
              data-testid="message-body"
            />
          </div>
        </div>
        <DialogFooter className="gap-2">
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={onSend} disabled={sending} data-testid="send-message-btn">
            {sending ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Mail className="w-4 h-4 mr-2" />}
            {targets.length === 1 ? 'Send Email' : `Send to ${targets.length}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ============== View Automata ==============
export function AutomataDialog({ open, onOpenChange, user, automata, loading }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{user?.name}'s Automata</DialogTitle>
          <DialogDescription>Viewing all automata for this user</DialogDescription>
        </DialogHeader>
        <ScrollArea className="max-h-[60vh]">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
            </div>
          ) : automata.length === 0 ? (
            <p className="text-center text-muted-foreground py-8">No automata found</p>
          ) : (
            <div className="space-y-4 p-2">
              {automata.map((a) => (
                <Card key={a.automaton_id}>
                  <CardHeader className="py-3">
                    <CardTitle className="text-base">{a.name}</CardTitle>
                    <CardDescription>
                      {a.type} &bull; {a.states?.length || 0} states &bull; {a.transitions?.length || 0} transitions
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="py-3">
                    <div className="text-xs font-mono bg-muted rounded p-2">
                      <p>Alphabet: {a.alphabet?.length > 0 ? `{${a.alphabet.join(', ')}}` : 'empty'}</p>
                      <p>States: {a.states?.length > 0 ? `{${a.states.map(s => s.name).join(', ')}}` : 'empty'}</p>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}

// ============== Invite Admin ==============
export function InviteAdminDialog({ open, onOpenChange, email, setEmail, loading, onInvite }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserPlus className="w-5 h-5 text-primary" /> Add Admin
          </DialogTitle>
          <DialogDescription>
            Enter an email address. If the person already has an account, they'll be promoted to admin immediately. If not, they'll receive an invitation to register as an admin.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2 py-2">
          <Label htmlFor="invite-email">Email address</Label>
          <Input
            id="invite-email" type="email" value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="admin@example.com" data-testid="invite-email-input"
          />
        </div>
        <DialogFooter className="gap-2">
          <Button variant="ghost" onClick={() => { onOpenChange(false); setEmail(''); }}>Cancel</Button>
          <Button onClick={onInvite} disabled={loading} data-testid="invite-submit-btn">
            {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <UserPlus className="w-4 h-4 mr-2" />}
            Send Invitation
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
